import { 
  auth, 
  EmailAuthProvider, 
  reauthenticateWithCredential,
  User
} from '../firebase/config';

class AuthService {
  /**
   * Reauthenticates the current user with their email and password
   * This will also refresh the user's token
   * @param email - User's email address
   * @param password - User's current password
   * @returns Promise that resolves when reauthentication is successful
   */
  async reauthenticateUser(email: string, password: string): Promise<void> {
    try {
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('No user is currently signed in');
      }

      // Verify the user is still authenticated before proceeding
      if (!user.emailVerified && !user.isAnonymous) {
        console.warn('User email not verified, but proceeding with reauthentication');
      }

      // Create credential with email and password
      const credential = EmailAuthProvider.credential(email, password);
      
      // Reauthenticate the user
      await reauthenticateWithCredential(user, credential);
      
      console.log('User reauthenticated successfully');
    } catch (error) {
      console.error('Reauthentication failed:', error);
      throw error;
    }
  }

  /**
   * Reauthenticates the current user with their password (uses current user's email)
   * @param password - User's current password
   * @returns Promise that resolves when reauthentication is successful
   */
  async reauthenticateWithPassword(password: string): Promise<void> {
    const user = auth.currentUser;
    
    if (!user || !user.email) {
      throw new Error('No user is currently signed in or user email not available');
    }

    try {
      return await this.reauthenticateUser(user.email, password);
    } catch (error) {
      console.error('Reauthentication failed:', error);
      // Re-throw the error with a more user-friendly message
      if (error instanceof Error) {
        throw new Error(error.message || 'Authentication failed. Please try again.');
      }
      throw new Error('Authentication failed. Please try again.');
    }
  }

  /**
   * Gets the current authenticated user
   * @returns Current user or null if not authenticated
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Checks if the user is currently authenticated
   * @returns true if user is authenticated, false otherwise
   */
  isAuthenticated(): boolean {
    return auth.currentUser !== null;
  }

  /**
   * Gets the current user's ID token (refreshed if needed)
   * @returns Promise that resolves to the ID token
   */
  async getIdToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    
    try {
      return await user.getIdToken(true); // Force refresh
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }
}

// Export a singleton instance
export const authService = new AuthService();
export default authService; 